import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { type UserSchema, type UserFormErrors, validateUser } from '@klear/ui/lib/user-form-schema'
import { useTraders } from '@klear/ui/hooks/use-traders'
import UserFormCommon from '@/app/user/UserFormCommon'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { ROUTE_USER } from '@klear/constants'
import { useUser, useUserMutations } from '@klear/ui/hooks/use-user'
import { userToFormSchema, userSchemaToUpdateWire } from '@klear/ui/lib/user-wire'
import isError from 'lodash/isError'
import isFinite from 'lodash/isFinite'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const UserEdit: React.FC = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()

  const { traders, isLoading: loadingTraders, error: tradersError } = useTraders()
  const { user, isLoading: loadingUser, error } = useUser(parseInt(userId || '0'))
  useDocumentTitle(
    user && [user.firstName, user.lastName].filter(Boolean).join(' '),
    'Edit User',
    traders?.find((trader) => trader.id === user?.roles?.[0]?.traderId)?.name
  )
  const { updateUser } = useUserMutations()

  const [values, setValues] = useState<UserSchema | null>(null)
  const [errors, setErrors] = useState<UserFormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user) setValues(userToFormSchema(user))
  }, [user])

  const onChange = (patch: Partial<UserSchema>) => setValues((prev) => (prev ? { ...prev, ...patch } : prev))

  const onSubmit = async () => {
    if (!user || user.id == null || !values) return

    const nextErrors: UserFormErrors = validateUser(values)

    const role = values.roles?.[0]
    if (!role?.role) {
      nextErrors.roles = 'User must have one role'
    } else if (role.traderId == null) {
      nextErrors.roles = 'Please select a trader'
    } else if (!isFinite(toNumber(role.traderId))) {
      nextErrors.roles = 'Invalid trader selection'
    }

    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return

    setSubmitting(true)
    try {
      await updateUser({ id: user.id, data: userSchemaToUpdateWire(values) })
      navigate(`/${ROUTE_USER}`)
    } catch (error) {
      console.error('Error updating user:', error)
      setErrors({
        root: isError(error) ? error.message : 'Failed to update user. Please try again.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (error || tradersError) {
    return (
      <div className="container mx-auto px-2 py-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">Error loading user</h2>
          <p className="text-muted-foreground">{error?.message ?? tradersError?.message}</p>
        </div>
      </div>
    )
  }

  if (loadingUser || !values) {
    return (
      <div className="container mx-auto px-2 py-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Loading user...</h2>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="container mx-auto px-2 py-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold">user not found</h2>
        </div>
      </div>
    )
  }

  return (
    <UserFormCommon
      values={values}
      errors={errors}
      onChange={onChange}
      isNewUser={false}
      traders={traders}
      onSubmit={onSubmit}
      cancelPath={`/${ROUTE_USER}/${userId}`}
      title="Edit User"
      loadingTraders={loadingTraders}
      loadingUser={loadingUser}
      submitting={submitting}
      currentUserIsSuperAdmin={currentUser?.isSuperAdmin}
    />
  )
}

export default UserEdit
